/* eslint-disable no-param-reassign */

import urljoin from 'url-join';
import { format, getTime } from 'date-fns';
import logger from '../../logger';

export const DIRECTORY_TYPE = 'directory';

export const createNode = (path, node) => ({
    ...node,
    path,
    isDirectory: node.type === DIRECTORY_TYPE,
    children: node.type === DIRECTORY_TYPE ? [] : null,
    initialized: false,
    loading: false,
});

export const updateNode = (node, children, other) => ({
    ...node,
    initialized: true,
    loading: false,
    ...other,
    children: children.map((child) => child.filename),
});

export const initializeNode = (pathOrPaths, nodes, setNodes, cloud, refresh = false) => {
    const paths = Array.isArray(pathOrPaths) ? pathOrPaths : [pathOrPaths];

    const isNew = paths.reduce((carry, path) => {
        if (!nodes[path]) {
            /* must be set directly to prevent concurrency issues */
            nodes[path] = createNode(path, {
                type: DIRECTORY_TYPE,
            });
            return true;
        }
        return carry;
    }, false);

    const { isChanged, results } = paths.reduce(
        (carry, path) => {
            const node = nodes[path];

            if ((!node.initialized || refresh) && !node.loading) {
                nodes[path].loading = true; /* must be set directly to prevent concurrency issues */

                carry.results.push(
                    cloud
                        .directory(node.path)
                        .then((dir) => ({ dir, path }))
                        .catch((error) => {
                            return { error, path };
                        })
                );
                carry.isChanged = true;
            }

            return carry;
        },
        { isChanged: false, results: [] }
    );

    if (isNew || isChanged) {
        setNodes({ ...nodes });

        return Promise.all(results).then((result) => {
            const err = result.reduce((carry, { dir = [], path, error }) => {
                dir.forEach((entry) => {
                    nodes[entry.filename] = createNode(entry.filename, entry);
                });

                nodes[path] = updateNode(nodes[path], dir, { error });

                return carry || error;
            }, false);

            setNodes({ ...nodes });

            if (err) {
                throw err;
            }
        });
    }

    return Promise.resolve();
};

export const groupPathsByType = (paths, nodes) =>
    paths.reduce(
        ([dirs, files], path) => {
            if (nodes[path].type === DIRECTORY_TYPE) {
                dirs.push(path);
            } else {
                files.push(path);
            }
            return [dirs, files];
        },
        [[], []]
    );

export const getNodeSortValue = (nodes) => (path, orderBy) => {
    const node = nodes[path];

    switch (orderBy) {
        case 'name':
            return node.filename.toLowerCase();
        case 'modified':
            return getTime(new Date(node.lastmod));
        default:
            return node.filename;
    }
};

export const asRelativePath = (path) => {
    if (path && path.startsWith('/') && path.length > 1) {
        return path.substring(1);
    }
    return path;
};

export const asAbsolutePath = (path) => {
    if (path && !path.startsWith('/')) {
        return `/${path}`;
    }
    return path;
};

export const getFileName = (filename) => {
    if (!filename) {
        return null;
    }

    const parts = filename.endsWith('/')
        ? filename.substring(0, filename.length - 1).split('/')
        : filename.split('/');
    const last = parts.pop();
    return last.length > 0 ? last : '/';
};

export const getFilePath = (filename, prefix = null) => {
    if (!filename) {
        return null;
    }

    const parts = filename.split('/');
    parts.pop(); // remove file name

    if (parts.length === 1 && parts[0] === '') {
        return prefix ? urljoin(prefix, '/') : '/';
    }

    return prefix ? urljoin(prefix, parts.join('/')) : parts.join('/');
};

// A stale (pre-fix) save could take an already-complete, correctly sanitized
// `path` and re-derive it as `urljoin(path, name)` from a mismatched
// name/path pair, permanently writing the corrupt name back as an *extra*
// trailing path segment, e.g.
//   ".../Report 2.pdf/Report (2).pdf"
// Once that happens, `getFileName(path)` returns the corrupt name too (it's
// now the literal last segment), so it agrees with the stored `name` and the
// mismatch below would never be noticed. Detect this by checking whether the
// segment "above" the last one is itself named like a file (has a known
// attachment extension) rather than a plain folder -- real per-email folders
// are never dotted -- and if so, strip the duplicate segment(s) to recover
// the real path. A named-extension allowlist (rather than "any dot") avoids
// false positives on legitimate dotted folder names (e.g. "v1.2").
const KNOWN_ATTACHMENT_EXTENSION = /\.(pdf|docx?|xlsx?|pptx?|txt|csv|zip|rar|7z|jpe?g|png|gif|bmp|tiff?|msg|eml|vcf|rtf|odt|ods|odp|xml|json|heic)$/i;

export const repairDoubledPath = (path) => {
    if (!path) {
        return path;
    }

    let repaired = path;
    let parent = getFilePath(repaired);
    while (parent && KNOWN_ATTACHMENT_EXTENSION.test(parent)) {
        repaired = parent;
        parent = getFilePath(repaired);
    }
    return repaired;
};

// The stored `path` is already sanitized, but many legacy `name` values are
// not, which leaves the two out of sync. Whenever they disagree, treat the
// sanitized path as the source of truth and re-derive the name from it.
// Comparing against the exact basename (rather than `path.endsWith(name)`)
// matters: a corrupted name that happens to be a suffix of the real,
// sanitized filename (e.g. "port2024.pdf" vs. "report2024.pdf") would
// otherwise pass as "already correct", and the mismatch would then surface
// downstream as the filename getting appended a second time onto the path.
export const withPathDerivedName = (attachment) => {
    if (!attachment || !attachment.path) {
        return attachment;
    }
    const repairedPath = repairDoubledPath(attachment.path);
    const pathName = getFileName(repairedPath);
    if (attachment.name === pathName && repairedPath === attachment.path) {
        return attachment;
    }
    return { ...attachment, path: repairedPath, name: pathName };
};
export const formatFileSize = (filesize) => {
    const kb = filesize / 1024;
    if (kb < 100) {
        return `${kb.toFixed(2)} KB`;
    }

    const mb = kb / 1024;
    if (mb < 100) {
        return `${mb.toFixed(2)} MB`;
    }

    const gb = mb / 1024;
    return `${gb.toFixed(2)} GB`;
};

export const attachmentsToFieldValue = (
    attachments = [],
    subtypeFilter = [null, 'signature', 'signature_premium'],
    filepathField = 'filepath',
    filenameField = 'filename'
) =>
    attachments
        .filter(
            (attachment) =>
                attachment.subtype === undefined || subtypeFilter.includes(attachment.subtype)
        )
        .map((attachment) => {
            const data =
                typeof attachment === 'string' ? { [filepathField]: attachment } : attachment;
            const sourcePath = data[filepathField] || data.filepath || data.path;
            const filename = getFileName(
                data[filenameField] || data.filename || data.name || data.basename || sourcePath
            );

            // Stored documents and email attachments contain the complete file
            // path. Some draft attachments already contain a directory plus a
            // separate filename, so complete that path before normalizing it.
            const filepath =
                sourcePath && filename && getFileName(sourcePath) !== filename
                    ? urljoin(sourcePath, filename)
                    : sourcePath;

            return {
                ...data,
                file: null,
                uploaded: true,
                filepath,
                path: getFilePath(filepath),
                filename,
            };
        });

export const filesToFieldValue = (files = [], initial = [], path = null, max = null) => {
    const values = files.reduce(
        (carry, file) => {
            const attachment = {
                file,
                filename: file.name,
                uploaded: false,
                path,
                metadata: {},
            };

            const index = carry.findIndex(
                (item) => item.file && item.file.path === attachment.file.path
            );
            if (index >= 0) {
                // eslint-disable-next-line no-param-reassign
                carry[index] = attachment;
            } else {
                carry.push(attachment);
            }
            return carry;
        },
        [...initial]
    );

    return max ? values.slice(0, max) : values;
};

const getUploadPath = (uploaded, path, filename, root, overwrite) => {
    if (!path || !filename) {
        return filename;
    }

    if (!overwrite && !uploaded) {
        const suffix = format(new Date(), 'ddMMyyHHmmss');

        const parts = filename.split('.');
        const beforeLast = Math.max(0, parts.length - 2);
        parts[beforeLast] = `${parts[beforeLast]} ${suffix}`;

        return urljoin(root, path, parts.join('.'));
    }

    return urljoin(root, path, filename);
};

export const fieldValueToUploadable = (values, root, onProgress, onError) => {
    const existing = values
        .filter(({ file, destroy }) => !file && !destroy)
        .map(({ path, filename, ...data }) => ({
            ...data,
            path: urljoin(root, path, filename),
            onError,
        }));

    const uploads = values
        .filter(({ file, destroy }) => file && !destroy)
        .map(({ uploaded, path, filename, file, metadata, overwrite, ...data }) => {
            const uploadPath = getUploadPath(uploaded, path, filename, root, overwrite);
            return {
                ...data,
                path: uploadPath,
                file,
                metadata,
                onProgress: (progress) => {
                    const percentage = (progress.loaded / progress.total) * 100;
                    logger.info(`upload ${filename}: ${percentage}%`);
                    if (onProgress) {
                        onProgress(percentage);
                    }
                },
                onError,
                config: {
                    overwrite,
                },
                replace: existing.some((_existing) => _existing.path === uploadPath),
            };
        });

    const destroys = values
        .filter(({ file, destroy, path, filename }) => {
            if (file || !destroy) {
                return false;
            }

            const uploadPath = urljoin(root, path, filename);
            const upload = uploads.find((_upload) => _upload.path === uploadPath);
            if (upload) {
                upload.replace = true;
            }
            return !upload;
        })
        .map(({ path, filename, ...data }) => ({
            ...data,
            path: urljoin(root, path, filename),
            onError,
        }));

    return [uploads, destroys, existing];
};

export const fieldValueToAttachments = (attachments) =>
    attachments.map((attachment) => ({
        id: attachment.id,
        name: attachment.name,
        path: urljoin(attachment.path, attachment.name),
        overwrite: attachment.overwrite,
    }));
